import React from 'react';
import styled from 'styled-components';
import ComponentDemo from '../../components/ComponentDemo';
import CodeBlock from '../../components/CodeBlock';

const PageHeader = styled.div`
  margin-bottom: 48px;
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 700;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 12px 0;
`;

const Subtitle = styled.p`
  font-size: 18px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  margin: 0;
  line-height: 1.6;
`;

const Section = styled.section`
  margin-bottom: 64px;
`;

const SectionTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0 0 8px 0;
`;

const SectionDescription = styled.p`
  font-size: 15px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  line-height: 1.7;
  margin: 0 0 32px 0;
`;

const FontStack = styled.div`
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255,255,255,0.03)' : '#F8FAFC'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.1)' : '#E2E8F0'};
  border-radius: 8px;
  padding: 20px;
  font-family: ${(props) => props.fontFamily};
  margin-bottom: 24px;
`;

const FontName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${(props) => (props.theme.name === 'dark' ? '#E2E8F0' : '#0F172A')};
  margin-bottom: 12px;
`;

const FontSample = styled.div`
  font-size: 24px;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin-bottom: 8px;
`;

const FontFamilyCode = styled.code`
  font-size: 11px;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  font-family: 'SF Mono', Monaco, Consolas, 'Courier New', monospace;
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.05)'};
  padding: 4px 8px;
  border-radius: 4px;
  display: block;
  overflow-x: auto;
`;

// Typography scale examples
const Display1 = styled.h1`
  font-size: 48px;
  font-weight: 700;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0;
  line-height: 1.2;
`;

const Display2 = styled.h2`
  font-size: 36px;
  font-weight: 700;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0;
  line-height: 1.2;
`;

const Heading1 = styled.h1`
  font-size: 32px;
  font-weight: 600;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0;
  line-height: 1.3;
`;

const Heading2 = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0;
  line-height: 1.4;
`;

const Heading3 = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0;
  line-height: 1.4;
`;

const Heading4 = styled.h4`
  font-size: 18px;
  font-weight: 600;
  color: ${(props) => (props.theme.name === 'dark' ? '#ffffff' : '#0F172A')};
  margin: 0;
  line-height: 1.5;
`;

const BodyLarge = styled.p`
  font-size: 18px;
  font-weight: 400;
  color: ${(props) => (props.theme.name === 'dark' ? '#E2E8F0' : '#334155')};
  margin: 0;
  line-height: 1.6;
`;

const BodyRegular = styled.p`
  font-size: 15px;
  font-weight: 400;
  color: ${(props) => (props.theme.name === 'dark' ? '#E2E8F0' : '#334155')};
  margin: 0;
  line-height: 1.6;
`;

const BodySmall = styled.p`
  font-size: 14px;
  font-weight: 400;
  color: ${(props) => (props.theme.name === 'dark' ? '#CBD5E1' : '#475569')};
  margin: 0;
  line-height: 1.6;
`;

const Caption = styled.p`
  font-size: 12px;
  font-weight: 400;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  margin: 0;
  line-height: 1.5;
`;

const Label = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: ${(props) => (props.theme.name === 'dark' ? '#64748B' : '#94A3B8')};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ScaleTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: 24px 0;
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255,255,255,0.02)' : '#ffffff'};
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.1)' : '#E2E8F0'};
  border-radius: 8px;
  overflow: hidden;
`;

const Th = styled.th`
  padding: 12px 16px;
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: ${(props) => (props.theme.name === 'dark' ? '#94A3B8' : '#64748B')};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${(props) =>
    props.theme.name === 'dark' ? 'rgba(255,255,255,0.05)' : '#F8FAFC'};
  border-bottom: 1px solid
    ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.1)' : '#E2E8F0'};
`;

const Td = styled.td`
  padding: 16px;
  font-size: 14px;
  color: ${(props) => (props.theme.name === 'dark' ? '#E2E8F0' : '#334155')};
  border-bottom: 1px solid
    ${(props) =>
      props.theme.name === 'dark' ? 'rgba(255,255,255,0.05)' : '#F1F5F9'};

  &:last-child {
    border-bottom: none;
  }
`;

const Typography = () => {
  return (
    <div>
      <PageHeader>
        <Title>Typography</Title>
        <Subtitle>
          Our typography system uses system fonts for optimal performance and
          native feel across all platforms, with a clear hierarchy for content
          organization.
        </Subtitle>
      </PageHeader>

      <Section>
        <SectionTitle>Font Families</SectionTitle>
        <SectionDescription>
          We use three font stacks optimized for different use cases.
        </SectionDescription>

        <FontStack fontFamily="-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif">
          <FontName>Primary Font Family</FontName>
          <FontSample>
            The quick brown fox jumps over the lazy dog
          </FontSample>
          <FontFamilyCode>
            -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica
            Neue", Arial, sans-serif
          </FontFamilyCode>
        </FontStack>

        <FontStack fontFamily="'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif">
          <FontName>Secondary Font Family</FontName>
          <FontSample>
            The quick brown fox jumps over the lazy dog
          </FontSample>
          <FontFamilyCode>
            "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI",
            Roboto, sans-serif
          </FontFamilyCode>
        </FontStack>

        <FontStack fontFamily="'SF Mono', Monaco, Consolas, 'Courier New', monospace">
          <FontName>Monospace Font Family</FontName>
          <FontSample>
            The quick brown fox jumps over the lazy dog
          </FontSample>
          <FontFamilyCode>
            "SF Mono", Monaco, Consolas, "Courier New", monospace
          </FontFamilyCode>
        </FontStack>

        <CodeBlock>
{`// Access font families from theme
const Text = styled.p\`
  font-family: \${props => props.theme.fonts.primary};
\`;

const Code = styled.code\`
  font-family: \${props => props.theme.fonts.monospace};
\`;`}
        </CodeBlock>
      </Section>

      <Section>
        <SectionTitle>Type Scale</SectionTitle>
        <SectionDescription>
          Our type scale provides a consistent hierarchy for all text elements.
        </SectionDescription>

        <ComponentDemo
          label="Display 1"
          description="48px / 700 weight - Hero headings, major page titles"
          align="flex-start"
          direction="column"
        >
          <Display1>Display 1 Heading</Display1>
        </ComponentDemo>

        <ComponentDemo
          label="Display 2"
          description="36px / 700 weight - Page titles, major sections"
          align="flex-start"
          direction="column"
        >
          <Display2>Display 2 Heading</Display2>
        </ComponentDemo>

        <ComponentDemo
          label="Heading 1"
          description="32px / 600 weight - Primary section headings"
          align="flex-start"
          direction="column"
        >
          <Heading1>Heading 1</Heading1>
        </ComponentDemo>

        <ComponentDemo
          label="Heading 2"
          description="24px / 600 weight - Secondary section headings"
          align="flex-start"
          direction="column"
        >
          <Heading2>Heading 2</Heading2>
        </ComponentDemo>

        <ComponentDemo
          label="Heading 3"
          description="20px / 600 weight - Subsection headings"
          align="flex-start"
          direction="column"
        >
          <Heading3>Heading 3</Heading3>
        </ComponentDemo>

        <ComponentDemo
          label="Heading 4"
          description="18px / 600 weight - Card titles, minor headings"
          align="flex-start"
          direction="column"
        >
          <Heading4>Heading 4</Heading4>
        </ComponentDemo>

        <ComponentDemo
          label="Body Large"
          description="18px / 400 weight - Emphasized body text, introductions"
          align="flex-start"
          direction="column"
        >
          <BodyLarge>
            This is large body text. Use for emphasized paragraphs and
            important content that needs more visual weight.
          </BodyLarge>
        </ComponentDemo>

        <ComponentDemo
          label="Body Regular"
          description="15px / 400 weight - Standard body text, primary content"
          align="flex-start"
          direction="column"
        >
          <BodyRegular>
            This is regular body text. Use for most paragraph content and
            general interface text. It provides good readability at comfortable
            sizes.
          </BodyRegular>
        </ComponentDemo>

        <ComponentDemo
          label="Body Small"
          description="14px / 400 weight - Secondary body text, table content"
          align="flex-start"
          direction="column"
        >
          <BodySmall>
            This is small body text. Use for secondary information, table cells,
            and compact interfaces where space is limited.
          </BodySmall>
        </ComponentDemo>

        <ComponentDemo
          label="Caption"
          description="12px / 400 weight - Meta information, timestamps"
          align="flex-start"
          direction="column"
        >
          <Caption>
            This is caption text. Use for timestamps, meta information, and
            supplementary details.
          </Caption>
        </ComponentDemo>

        <ComponentDemo
          label="Label"
          description="11px / 600 weight uppercase - Form labels, section labels"
          align="flex-start"
          direction="column"
        >
          <Label>Label Text</Label>
        </ComponentDemo>
      </Section>

      <Section>
        <SectionTitle>Type Scale Reference</SectionTitle>
        <SectionDescription>
          Quick reference table for all typography styles.
        </SectionDescription>

        <ScaleTable>
          <thead>
            <tr>
              <Th>Style</Th>
              <Th>Size</Th>
              <Th>Weight</Th>
              <Th>Line Height</Th>
              <Th>Usage</Th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <Td>Display 1</Td>
              <Td>48px</Td>
              <Td>700 (Bold)</Td>
              <Td>1.2</Td>
              <Td>Hero headings</Td>
            </tr>
            <tr>
              <Td>Display 2</Td>
              <Td>36px</Td>
              <Td>700 (Bold)</Td>
              <Td>1.2</Td>
              <Td>Page titles</Td>
            </tr>
            <tr>
              <Td>Heading 1</Td>
              <Td>32px</Td>
              <Td>600 (Semibold)</Td>
              <Td>1.3</Td>
              <Td>Primary headings</Td>
            </tr>
            <tr>
              <Td>Heading 2</Td>
              <Td>24px</Td>
              <Td>600 (Semibold)</Td>
              <Td>1.4</Td>
              <Td>Secondary headings</Td>
            </tr>
            <tr>
              <Td>Heading 3</Td>
              <Td>20px</Td>
              <Td>600 (Semibold)</Td>
              <Td>1.4</Td>
              <Td>Subsection headings</Td>
            </tr>
            <tr>
              <Td>Heading 4</Td>
              <Td>18px</Td>
              <Td>600 (Semibold)</Td>
              <Td>1.5</Td>
              <Td>Card titles</Td>
            </tr>
            <tr>
              <Td>Body Large</Td>
              <Td>18px</Td>
              <Td>400 (Regular)</Td>
              <Td>1.6</Td>
              <Td>Emphasized body</Td>
            </tr>
            <tr>
              <Td>Body Regular</Td>
              <Td>15px</Td>
              <Td>400 (Regular)</Td>
              <Td>1.6</Td>
              <Td>Standard body</Td>
            </tr>
            <tr>
              <Td>Body Small</Td>
              <Td>14px</Td>
              <Td>400 (Regular)</Td>
              <Td>1.6</Td>
              <Td>Secondary body</Td>
            </tr>
            <tr>
              <Td>Caption</Td>
              <Td>12px</Td>
              <Td>400 (Regular)</Td>
              <Td>1.5</Td>
              <Td>Meta information</Td>
            </tr>
            <tr>
              <Td>Label</Td>
              <Td>11px</Td>
              <Td>600 (Semibold)</Td>
              <Td>1.4</Td>
              <Td>Form labels</Td>
            </tr>
          </tbody>
        </ScaleTable>
      </Section>

      <Section>
        <SectionTitle>Usage Example</SectionTitle>
        <CodeBlock>
{`import styled from 'styled-components';

// Display styles
const PageTitle = styled.h1\`
  font-size: 36px;
  font-weight: 700;
  color: \${props => props.theme.colors.text.primary};
  line-height: 1.2;
  margin: 0 0 12px 0;
\`;

// Heading styles
const SectionTitle = styled.h2\`
  font-size: 24px;
  font-weight: 600;
  color: \${props => props.theme.colors.text.primary};
  line-height: 1.4;
  margin: 0 0 16px 0;
\`;

// Body styles
const BodyText = styled.p\`
  font-size: 15px;
  font-weight: 400;
  color: \${props => props.theme.colors.text.secondary};
  line-height: 1.6;
  margin: 0;
\`;

// Label styles
const FormLabel = styled.label\`
  font-size: 11px;
  font-weight: 600;
  color: \${props => props.theme.colors.text.muted};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: block;
  margin-bottom: 8px;
\`;`}
        </CodeBlock>
      </Section>
    </div>
  );
};

export default Typography;